/**
 * Teen Patti Game Component
 * Three-card poker variant with Blind/Seen betting
 * Supports Dealer's Choice variations: Classic, Muflis, AK47, Slutty Queens, 
 * One-Eyed Jacks, Lowest Card Joker, Random Joker, 999, and Community cards
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Eye, EyeOff, HelpCircle, Trophy, X, Users, Coins, History, Settings, Sparkles, Star, Target, Layers, Shuffle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import useTeenPattiStore, { PLAYER_STATUS, GAME_PHASE, DEALER_CHOICE_VARIATIONS, evaluateHand, getVariationsByCategory } from './teenPattiStore';

// Boosty Games Logo URL
const BOOSTY_LOGO_URL = "https://customer-assets.emergentagent.com/job_4e3de3c9-461d-4f73-ada0-30b07137d950/artifacts/0wd7buyq_Boosty%20Games%20v2.png";

// Suit colors matching poker
const SUIT_COLORS = {
  hearts: '#dc2626',
  diamonds: '#dc2626',
  clubs: '#1e293b',
  spades: '#1e293b'
};

// Card Component with Boosty Games styling
const Card = ({ card, faceUp = true, small = false, dealDelay = 0 }) => {
  if (!card) return null;
  
  const isJoker = card.isJoker;
  const suitColor = SUIT_COLORS[card.suit] || '#1e293b';
  const sizeClass = small ? 'w-12 h-16' : 'w-16 h-24';
  
  // Card Back - Boosty Games branded
  if (!faceUp) {
    return (
      <motion.div 
        initial={{ opacity: 0, rotateY: 180, y: -20 }}
        animate={{ opacity: 1, rotateY: 0, y: 0 }}
        transition={{ duration: 0.3, delay: dealDelay, type: 'spring', damping: 15 }}
        className={`${sizeClass} rounded-lg relative overflow-hidden`}
        style={{
          background: 'linear-gradient(135deg, #7c3aed 0%, #4c1d95 50%, #1e1b4b 100%)',
          border: '2px solid #a855f7',
          boxShadow: '0 2px 8px rgba(139,92,246,0.3)'
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-orange-400/40 to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center">
          <img src={BOOSTY_LOGO_URL} alt="" className={`${small ? 'h-8' : 'h-12'} w-auto object-contain opacity-90`} />
        </div>
      </motion.div>
    );
  }
  
  // Card Front
  return (
    <motion.div
      initial={{ opacity: 0, rotateY: 180, y: -20 }}
      animate={{ opacity: 1, rotateY: 0, y: 0 }}
      transition={{ duration: 0.3, delay: dealDelay, type: 'spring', damping: 15 }}
      className={`${sizeClass} rounded-lg relative bg-white ${isJoker ? 'ring-2 ring-yellow-400 shadow-[0_0_12px_rgba(234,179,8,0.5)]' : ''}`}
      style={{ border: '1px solid #cbd5e1', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }}
    >
      {/* Joker star indicator */}
      {isJoker && (
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-400 rounded-full flex items-center justify-center z-10">
          <Star className="w-3 h-3 text-yellow-900" />
        </div>
      )}
      
      {/* Top left rank/suit */}
      <div className="absolute top-1 left-1.5 flex flex-col items-center leading-none">
        <span className={`${small ? 'text-sm' : 'text-base'} font-black`} style={{ color: suitColor }}>{card.rank}</span>
        <span className={`${small ? 'text-xs' : 'text-sm'}`} style={{ color: suitColor }}>{card.symbol}</span>
      </div>
      
      {/* Center suit */}
      <div className="absolute inset-0 flex items-center justify-center">
        <span className={`${small ? 'text-xl' : 'text-3xl'}`} style={{ color: suitColor }}>{card.symbol}</span>
      </div>
      
      {/* Bottom right rank/suit (rotated) */}
      <div className="absolute bottom-1 right-1.5 flex flex-col items-center leading-none rotate-180">
        <span className={`${small ? 'text-sm' : 'text-base'} font-black`} style={{ color: suitColor }}>{card.rank}</span>
        <span className={`${small ? 'text-xs' : 'text-sm'}`} style={{ color: suitColor }}>{card.symbol}</span>
      </div>
    </motion.div>
  );
};

// Chip flying animation
const ChipAnimation = ({ fromPosition, amount }) => {
  if (!fromPosition) return null;
  
  return (
    <motion.div
      initial={{ ...fromPosition, scale: 1, opacity: 1 }}
      animate={{ 
        x: '50%', 
        y: '50%', 
        scale: 0.5, 
        opacity: 0 
      }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="fixed z-50 pointer-events-none"
    >
      <div className="flex items-center gap-1 bg-amber-500 text-black font-bold px-2 py-1 rounded-full text-sm">
        <Coins className="w-4 h-4" />
        ₹{amount}
      </div>
    </motion.div>
  );
};

// Hand display with staggered dealing animation
const HandDisplay = ({ cards, faceUp = true, small = false, animate = false }) => {
  return (
    <div className="flex gap-1">
      {cards.map((card, idx) => (
        <Card 
          key={card.id} 
          card={card} 
          faceUp={faceUp} 
          small={small}
          dealDelay={animate ? idx * 0.1 : 0}
        />
      ))}
    </div>
  );
};

// Player seat component
const PlayerSeat = ({ player, isCurrentPlayer, isDealer, position, showCards, variation, revealedJoker, dealingAnimation }) => {
  const statusColors = {
    [PLAYER_STATUS.ACTIVE_BLIND]: 'border-purple-500 bg-purple-900/30',
    [PLAYER_STATUS.ACTIVE_SEEN]: 'border-cyan-500 bg-cyan-900/30',
    [PLAYER_STATUS.PACKED]: 'border-slate-600 bg-slate-800/50 opacity-60',
    [PLAYER_STATUS.WAITING]: 'border-slate-600 bg-slate-800/30'
  };
  
  const statusLabels = {
    [PLAYER_STATUS.ACTIVE_BLIND]: { text: 'BLIND', color: 'text-purple-400' },
    [PLAYER_STATUS.ACTIVE_SEEN]: { text: 'SEEN', color: 'text-cyan-400' },
    [PLAYER_STATUS.PACKED]: { text: 'PACKED', color: 'text-slate-500' },
    [PLAYER_STATUS.WAITING]: { text: '', color: '' }
  };
  
  const positionStyles = {
    bottom: 'bottom-4 left-1/2 -translate-x-1/2',
    top: 'top-4 left-1/2 -translate-x-1/2',
    left: 'left-4 top-1/2 -translate-y-1/2',
    right: 'right-4 top-1/2 -translate-y-1/2',
    topLeft: 'top-16 left-16',
    topRight: 'top-16 right-16'
  };
  
  // Human player only sees cards face-up if they've chosen to see them (ACTIVE_SEEN status)
  // Or during showdown (showCards=true)
  const canSeeCards = showCards || (player.isHuman && player.status === PLAYER_STATUS.ACTIVE_SEEN);
  
  const hand = player.cards.length >= 2 ? evaluateHand(player.cards, variation, { revealedJoker }) : null;
  
  return (
    <motion.div
      className={`absolute ${positionStyles[position]}`}
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
    >
      <div className={`rounded-xl border-2 p-3 ${statusColors[player.status]} ${isCurrentPlayer ? 'ring-2 ring-amber-400 ring-offset-2 ring-offset-slate-900' : ''}`}>
        {/* Player info */}
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white font-bold text-sm">
            {player.name[0]}
          </div>
          <div>
            <div className="text-white font-semibold text-sm flex items-center gap-1">
              {player.name}
              {isDealer && <span className="text-[10px] bg-amber-500 text-black px-1 rounded">D</span>}
              {player.isMiniBootPosition && <span className="text-[10px] bg-purple-500 text-white px-1 rounded">MB</span>}
              {player.isBootPosition && <span className="text-[10px] bg-cyan-500 text-white px-1 rounded">B</span>}
            </div>
            <div className="text-amber-400 text-xs font-mono">₹{player.chips}</div>
          </div>
        </div>
        
        {/* Cards - human only sees face-up after choosing "See Cards" */}
        {player.cards.length > 0 && (
          <div className="mb-2">
            <HandDisplay 
              cards={player.cards} 
              faceUp={canSeeCards} 
              small={!player.isHuman}
              animate={dealingAnimation}
            />
          </div>
        )}
        
        {/* Hand name for human player - only when they've seen their cards */}
        {player.isHuman && hand && player.status === PLAYER_STATUS.ACTIVE_SEEN && (
          <div className="text-xs text-emerald-400 font-semibold text-center mb-1">
            {hand.name}
          </div>
        )}
        
        {/* Status and bet */}
        <div className="flex justify-between items-center text-xs">
          <span className={statusLabels[player.status].color}>
            {statusLabels[player.status].text}
          </span>
          {player.totalBet > 0 && (
            <span className="text-amber-300">Bet: ₹{player.totalBet}</span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

// Action buttons for human player - compact version
const ActionPanel = ({ 
  player, 
  currentStake, 
  config, 
  onSeeCards, 
  onBet, 
  onPack, 
  onSideShow, 
  onShow,
  canSideShow,
  canShow,
  disabled 
}) => {
  const [betAmount, setBetAmount] = useState(0);
  
  const isBlind = player.status === PLAYER_STATUS.ACTIVE_BLIND;
  const minBet = isBlind ? currentStake * config.blindBetMin : currentStake * config.seenBetMin;
  const maxBet = Math.min(
    isBlind ? currentStake * config.blindBetMax : currentStake * config.seenBetMax,
    player.chips
  );
  
  useEffect(() => {
    setBetAmount(minBet);
  }, [minBet]);
  
  if (disabled) {
    return (
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-lg p-2 text-center">
        <div className="text-slate-400 text-sm">Waiting...</div>
      </div>
    );
  }
  
  return (
    <div className="bg-slate-800/80 backdrop-blur-sm rounded-lg p-2 space-y-2">
      <div className="text-center text-xs text-slate-300">
        Stake: <span className="text-amber-400 font-bold">₹{currentStake}</span>
        {isBlind && <span className="text-purple-400 ml-1">(Blind)</span>}
      </div>
      
      {/* See Cards button (only for blind players) */}
      {isBlind && (
        <Button
          onClick={onSeeCards}
          size="sm"
          className="w-full bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400"
        >
          <Eye className="w-4 h-4 mr-1" />
          See Cards
        </Button>
      )}
      
      {/* Bet slider and button */}
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400">₹{minBet}</span>
          <input
            type="range"
            min={minBet}
            max={maxBet}
            value={betAmount}
            onChange={(e) => setBetAmount(parseInt(e.target.value))}
            className="flex-1 accent-amber-500 h-1"
          />
          <span className="text-[10px] text-slate-400">₹{maxBet}</span>
        </div>
        <Button
          onClick={() => onBet(betAmount)}
          size="sm"
          className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400"
        >
          <Coins className="w-4 h-4 mr-1" />
          {isBlind ? 'Blind' : 'Chaal'} ₹{betAmount}
        </Button>
      </div>
      
      {/* Side Show / Show / Pack row */}
      <div className="flex gap-2">
        {canSideShow && (
          <Button
            onClick={onSideShow}
            size="sm"
            variant="outline"
            className="flex-1 border-purple-500 text-purple-400 hover:bg-purple-500/20 text-xs px-2"
          >
            Side Show
          </Button>
        )}
        
        {canShow && (
          <Button
            onClick={onShow}
            size="sm"
            className="flex-1 bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-xs"
          >
            <Trophy className="w-3 h-3 mr-1" />
            Show
          </Button>
        )}
        
        <Button
          onClick={onPack}
          size="sm"
          variant="ghost"
          className="text-rose-400 hover:bg-rose-500/20 text-xs px-3"
        >
          Pack
        </Button>
      </div>
    </div>
  );
};

// Side Show modal
const SideShowModal = ({ request, players, onAccept, onDecline, isResponder }) => {
  if (!request) return null;
  
  const requester = players[request.fromIndex];
  const responder = players[request.toIndex];
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-gradient-to-br from-purple-900 to-slate-900 border-2 border-purple-500 rounded-2xl p-6 max-w-md text-center"
      >
        <Users className="w-12 h-12 text-purple-400 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-white mb-2">Side Show Request</h3>
        <p className="text-slate-300 mb-4">
          {requester.name} wants to compare hands with {responder.name}
        </p>
        
        {isResponder && (
          <div className="flex gap-3 justify-center">
            <Button
              onClick={onAccept}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              Accept
            </Button>
            <Button
              onClick={onDecline}
              variant="outline"
              className="border-red-500 text-red-400"
            >
              Decline
            </Button>
          </div>
        )}
        
        {!isResponder && (
          <div className="text-amber-400">Waiting for response...</div>
        )}
      </motion.div>
    </motion.div>
  );
};

// Winner overlay
const WinnerOverlay = ({ winner, pot, showdownHands, players, onNextHand }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ scale: 0.8, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        className="bg-gradient-to-br from-amber-900 to-slate-900 border-2 border-amber-500 rounded-2xl p-8 max-w-lg text-center"
      >
        <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
        <h2 className="text-3xl font-bold text-white mb-2">{winner.name} Wins!</h2>
        <p className="text-amber-300 text-2xl font-mono mb-4">₹{pot}</p>
        
        {showdownHands && (
          <div className="space-y-4 mb-6">
            {Object.entries(showdownHands).map(([idx, hand]) => {
              const player = players[parseInt(idx)];
              const isWinner = player.id === winner.id;
              return (
                <div 
                  key={idx} 
                  className={`p-3 rounded-lg ${isWinner ? 'bg-emerald-900/50 border border-emerald-500' : 'bg-slate-800/50'}`}
                >
                  <div className="text-sm text-slate-300 mb-1">{player.name}</div>
                  <div className="flex justify-center gap-1 mb-1">
                    {player.cards.map((card, i) => (
                      <Card key={i} card={card} small />
                    ))}
                  </div>
                  <div className={`text-sm font-semibold ${isWinner ? 'text-emerald-400' : 'text-slate-400'}`}>
                    {hand.name}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        <Button
          onClick={onNextHand}
          className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 font-bold"
        >
          Next Hand
        </Button>
      </motion.div>
    </motion.div>
  );
};

// Help Modal
const HelpModal = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.9 }}
      animate={{ scale: 1 }}
      className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-amber-500 rounded-2xl p-6 max-w-lg max-h-[80vh] overflow-y-auto"
      onClick={e => e.stopPropagation()}
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-white">How to Play Teen Patti</h2>
        <button onClick={onClose} className="text-slate-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>
      
      <div className="space-y-4 text-gray-300 text-sm">
        <div className="bg-slate-800/50 rounded-xl p-4">
          <h3 className="font-semibold text-amber-400 mb-2">Overview</h3>
          <p>Each player receives 3 cards. Bet to stay in or pack to fold. Last player standing or best hand at showdown wins!</p>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4">
          <h3 className="font-semibold text-purple-400 mb-2">Blind vs Seen</h3>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Blind:</strong> Bet without seeing cards (cheaper bets)</li>
            <li><strong>Seen:</strong> After viewing cards (must bet 2x stake)</li>
          </ul>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4">
          <h3 className="font-semibold text-cyan-400 mb-2">Hand Rankings (Best to Worst)</h3>
          <ol className="list-decimal list-inside space-y-1">
            <li><strong>Trail:</strong> Three of a kind (A-A-A best)</li>
            <li><strong>Pure Sequence:</strong> Straight flush (A-K-Q same suit)</li>
            <li><strong>Sequence:</strong> Straight (A-K-Q mixed suits)</li>
            <li><strong>Color:</strong> Flush (3 same suit)</li>
            <li><strong>Pair:</strong> Two of a kind</li>
            <li><strong>High Card:</strong> Highest card wins</li>
          </ol>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4">
          <h3 className="font-semibold text-emerald-400 mb-2">Special Actions</h3>
          <ul className="list-disc list-inside space-y-1">
            <li><strong>Side Show:</strong> Challenge previous Seen player (if &gt;2 active)</li>
            <li><strong>Show:</strong> Final showdown when 2 players remain</li>
            <li><strong>Pack:</strong> Fold and forfeit your bets</li>
          </ul>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

// Action History Panel
const ActionHistoryPanel = ({ history }) => (
  <div className="bg-slate-900/80 backdrop-blur-sm rounded-xl p-3 max-h-40 overflow-y-auto">
    <div className="flex items-center gap-2 text-slate-400 text-xs mb-2">
      <History className="w-3 h-3" />
      Action History
    </div>
    <div className="space-y-1">
      {history.slice(-8).map((action, idx) => (
        <div key={idx} className="text-xs text-slate-300">
          <span className="text-amber-400">{action.player}</span>{' '}
          {action.action}
          {action.amount && <span className="text-emerald-400"> ₹{action.amount}</span>}
        </div>
      ))}
    </div>
  </div>
);

// Settings Modal - Dealer's Choice Variations
const SettingsModal = ({ onClose, variation, onVariationChange }) => {
  const categories = getVariationsByCategory();
  const [activeTab, setActiveTab] = useState('standard');
  
  const categoryConfig = {
    standard: { name: 'Standard', icon: Sparkles, color: 'amber' },
    wildcard: { name: 'Wildcards', icon: Star, color: 'yellow' },
    target: { name: 'Target', icon: Target, color: 'emerald' },
    community: { name: 'Community', icon: Layers, color: 'cyan' }
  };
  
  const variationColors = {
    CLASSIC: 'amber',
    MUFLIS: 'rose',
    AK47: 'yellow',
    SLUTTY_QUEENS: 'pink',
    ONE_EYED_JACKS: 'purple',
    LOWEST_CARD_JOKER: 'orange',
    TWO_LOWEST_JOKERS: 'orange',
    RANDOM_JOKER: 'teal',
    NEXT_CARD_JOKER: 'teal',
    NINE_NINE_NINE: 'emerald',
    COMMUNITY_1: 'cyan',
    COMMUNITY_2: 'cyan',
    COMMUNITY_3: 'cyan'
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-amber-500 rounded-2xl p-6 max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Shuffle className="w-5 h-5 text-amber-400" />
            Dealer's Choice
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Category Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {Object.entries(categoryConfig).map(([key, cfg]) => {
            const Icon = cfg.icon;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === key 
                    ? `bg-${cfg.color}-500/20 text-${cfg.color}-400 border border-${cfg.color}-500/50` 
                    : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                }`}
                style={activeTab === key ? {
                  backgroundColor: `rgba(var(--${cfg.color}-rgb, 245, 158, 11), 0.2)`,
                  color: cfg.color === 'amber' ? '#fbbf24' : cfg.color === 'yellow' ? '#facc15' : cfg.color === 'emerald' ? '#10b981' : cfg.color === 'cyan' ? '#06b6d4' : '#f97316'
                } : {}}
              >
                <Icon className="w-4 h-4" />
                {cfg.name}
              </button>
            );
          })}
        </div>
        
        {/* Variations Grid */}
        <div className="overflow-y-auto flex-1 pr-2">
          <div className="grid gap-3">
            {categories[activeTab]?.map(v => {
              const isSelected = variation === v.id;
              const color = variationColors[v.id] || 'slate';
              
              return (
                <div 
                  key={v.id}
                  className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                    isSelected 
                      ? `border-${color}-500 bg-${color}-500/10` 
                      : 'border-slate-600 hover:border-slate-500'
                  }`}
                  style={isSelected ? {
                    borderColor: color === 'amber' ? '#f59e0b' : color === 'yellow' ? '#eab308' : color === 'rose' ? '#f43f5e' : color === 'pink' ? '#ec4899' : color === 'purple' ? '#a855f7' : color === 'orange' ? '#f97316' : color === 'teal' ? '#14b8a6' : color === 'emerald' ? '#10b981' : color === 'cyan' ? '#06b6d4' : '#64748b',
                    backgroundColor: `rgba(${color === 'amber' ? '245, 158, 11' : color === 'yellow' ? '234, 179, 8' : color === 'rose' ? '244, 63, 94' : color === 'pink' ? '236, 72, 153' : color === 'purple' ? '168, 85, 247' : color === 'orange' ? '249, 115, 22' : color === 'teal' ? '20, 184, 166' : color === 'emerald' ? '16, 185, 129' : color === 'cyan' ? '6, 182, 212' : '100, 116, 139'}, 0.1)`
                  } : {}}
                  onClick={() => onVariationChange(v.id)}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-white font-semibold mb-1 flex items-center gap-2">
                      {v.name}
                      {v.wildcardType && (
                        <span className="text-[10px] bg-yellow-500/30 text-yellow-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <Star className="w-3 h-3" /> Joker
                        </span>
                      )}
                      {v.reversedRanking && (
                        <span className="text-[10px] bg-rose-500/30 text-rose-400 px-2 py-0.5 rounded">
                          Reverse
                        </span>
                      )}
                      {v.targetMode && (
                        <span className="text-[10px] bg-emerald-500/30 text-emerald-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <Target className="w-3 h-3" /> Target
                        </span>
                      )}
                      {v.category === 'community' && (
                        <span className="text-[10px] bg-cyan-500/30 text-cyan-400 px-2 py-0.5 rounded flex items-center gap-1">
                          <Layers className="w-3 h-3" /> Shared
                        </span>
                      )}
                    </h3>
                    {isSelected && (
                      <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </div>
                    )}
                  </div>
                  <p className="text-sm text-slate-400">{v.description}</p>
                </div>
              );
            })}
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-slate-700">
          <Button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400"
          >
            Apply Variation
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Teen Patti Component
const TeenPatti = () => {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [numPlayers, setNumPlayers] = useState(4);
  const [selectedVariation, setSelectedVariation] = useState('CLASSIC');
  const [potLimit, setPotLimit] = useState(null);
  
  // Show help on first visit
  useEffect(() => {
    const hasVisited = localStorage.getItem('teenpatti_visited');
    if (!hasVisited) {
      setShowHelp(true);
      localStorage.setItem('teenpatti_visited', 'true');
    }
  }, []);
  
  const {
    players,
    gamePhase,
    pot,
    currentStake,
    currentPlayerIndex,
    config,
    variation,
    variationConfig,
    sideShowRequest,
    actionHistory,
    winner,
    showdownHands,
    message,
    dealingAnimation,
    chipAnimation,
    potLimitReached,
    revealedJoker,
    communityCards,
    initGame,
    startHand,
    seeCards,
    placeBet,
    pack,
    requestSideShow,
    respondToSideShow,
    requestShow,
    nextHand,
    setVariation,
    setPotLimit: setStorePotLimit
  } = useTeenPattiStore();
  
  // Initialize game on mount
  useEffect(() => {
    if (players.length === 0) {
      initGame(numPlayers, 1000, selectedVariation);
    }
  }, [players.length, numPlayers, initGame, selectedVariation]);
  
  const humanPlayer = players.find(p => p.isHuman);
  const isHumanTurn = humanPlayer && currentPlayerIndex === 0 && gamePhase === GAME_PHASE.BETTING;
  
  const activePlayers = useMemo(() => 
    players.filter(p => p.status === PLAYER_STATUS.ACTIVE_BLIND || p.status === PLAYER_STATUS.ACTIVE_SEEN),
    [players]
  );
  
  const canSideShow = useMemo(() => {
    if (!humanPlayer || humanPlayer.status !== PLAYER_STATUS.ACTIVE_SEEN) return false;
    if (activePlayers.length <= 2) return false;
    // Check if there's a previous seen player
    return players.some((p, idx) => 
      idx !== 0 && p.status === PLAYER_STATUS.ACTIVE_SEEN
    );
  }, [humanPlayer, activePlayers, players]);
  
  const canShow = activePlayers.length === 2 && isHumanTurn;
  
  // Get current variation display info
  const currentVariationConfig = DEALER_CHOICE_VARIATIONS[variation] || DEALER_CHOICE_VARIATIONS.CLASSIC;
  
  // Position players around the table
  const getPlayerPosition = (idx, total) => {
    const positions = {
      4: ['bottom', 'left', 'top', 'right'],
      5: ['bottom', 'left', 'topLeft', 'topRight', 'right'],
      6: ['bottom', 'left', 'topLeft', 'top', 'topRight', 'right']
    };
    return positions[total]?.[idx] || 'bottom';
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-amber-950/20 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b-2 border-amber-700/50 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white flex items-center gap-2">
                Teen Patti
                {variation !== 'CLASSIC' && (
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    currentVariationConfig.reversedRanking ? 'bg-rose-500/20 text-rose-400' :
                    currentVariationConfig.wildcardType ? 'bg-yellow-500/20 text-yellow-400' :
                    currentVariationConfig.targetMode ? 'bg-emerald-500/20 text-emerald-400' :
                    currentVariationConfig.category === 'community' ? 'bg-cyan-500/20 text-cyan-400' :
                    'bg-amber-500/20 text-amber-400'
                  }`}>
                    {currentVariationConfig.name}
                  </span>
                )}
              </h1>
              <p className="text-xs text-amber-400">
                {currentVariationConfig.description || 'Three Card Poker'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {/* Revealed Joker indicator */}
            {revealedJoker && (
              <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg px-3 py-1.5 flex items-center gap-2">
                <Star className="w-4 h-4 text-yellow-400" />
                <span className="text-xs text-yellow-400 font-semibold">
                  Wild: {revealedJoker.rank}{revealedJoker.symbol}
                </span>
              </div>
            )}
            
            {/* Pot limit indicator */}
            {config.maxPot && (
              <div className="text-xs text-slate-400">
                Limit: ₹{config.maxPot}
                {potLimitReached && <span className="text-red-400 ml-1">(REACHED)</span>}
              </div>
            )}
            
            {/* Pot display */}
            <div className="bg-slate-800 rounded-lg px-4 py-2">
              <div className="text-xs text-slate-400">Pot</div>
              <div className="text-lg font-bold text-amber-400">₹{pot}</div>
            </div>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(true)}
              className="text-slate-400 hover:text-white"
              title="Dealer's Choice"
              data-testid="dealer-choice-btn"
            >
              <Shuffle className="w-5 h-5" />
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowHelp(true)}
              className="text-slate-400 hover:text-white"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
            
            {/* Multiplayer Lobby link */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/games/teen-patti/lobby')}
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
            >
              <Users className="w-4 h-4 mr-2" />
              Multiplayer
            </Button>
          </div>
        </div>
      </div>
      
      {/* Game Area - adjusted height to account for fixed bottom panel */}
      <div className="relative max-w-5xl mx-auto h-[calc(100vh-300px)] p-4">
        {/* Table */}
        <div className="absolute inset-4 rounded-[80px] bg-gradient-to-br from-emerald-900 to-emerald-950 border-8 border-amber-800 shadow-2xl">
          {/* Table felt pattern */}
          <div className="absolute inset-0 rounded-[72px] opacity-20 bg-[radial-gradient(circle_at_center,_transparent_0%,_rgba(0,0,0,0.3)_100%)]" />
          
          {/* Community Cards Display */}
          {communityCards && communityCards.length > 0 && (
            <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2">
              <div className="text-xs text-cyan-400 text-center mb-2 font-semibold">Community Cards</div>
              <div className="flex gap-2">
                {communityCards.map((card, idx) => (
                  <Card key={card.id || idx} card={card} faceUp={true} small={true} dealDelay={idx * 0.15} />
                ))}
              </div>
            </div>
          )}
          
          {/* Center pot display */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-center">
            <div className="text-3xl font-bold text-amber-400">₹{pot}</div>
            <div className="text-sm text-emerald-300 mt-1">{message}</div>
            
            {/* 999 Target indicator */}
            {currentVariationConfig.targetMode && (
              <div className="mt-2 bg-emerald-500/20 border border-emerald-500/50 rounded-lg px-3 py-1 inline-flex items-center gap-2">
                <Target className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-400 font-bold">Target: {currentVariationConfig.targetNumber}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Player seats */}
        {players.map((player, idx) => (
          <PlayerSeat
            key={player.id}
            player={player}
            isCurrentPlayer={idx === currentPlayerIndex}
            isDealer={player.isButton}
            position={getPlayerPosition(idx, players.length)}
            showCards={gamePhase === GAME_PHASE.SETTLING && showdownHands}
            variation={variation}
            revealedJoker={revealedJoker}
            dealingAnimation={dealingAnimation}
          />
        ))}
        
        {/* Chip Animation */}
        <AnimatePresence>
          {chipAnimation && (
            <ChipAnimation 
              fromPosition={{ x: 0, y: 0 }} 
              amount={chipAnimation.amount} 
            />
          )}
        </AnimatePresence>
      </div>
      
      {/* Bottom panel - compact */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-sm border-t border-slate-700 p-3">
        <div className="max-w-5xl mx-auto flex gap-4 items-center">
          {/* Action history - compact */}
          <div className="flex-1 max-h-24 overflow-y-auto">
            <ActionHistoryPanel history={actionHistory} />
          </div>
          
          {/* Action buttons or Start button */}
          <div className="w-72">
            {gamePhase === GAME_PHASE.WAITING ? (
              <div className="flex flex-col gap-2">
                {/* Compact row: variation + players + pot limit */}
                <div className="flex items-center gap-2 text-xs">
                  <Button
                    onClick={() => setShowSettings(true)}
                    variant="outline"
                    size="sm"
                    className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10 flex items-center gap-1 px-2"
                    data-testid="select-variation-btn"
                  >
                    <Shuffle className="w-3 h-3" />
                    {currentVariationConfig.name || 'Classic'}
                  </Button>
                  <select
                    value={numPlayers}
                    onChange={(e) => {
                      const n = parseInt(e.target.value);
                      setNumPlayers(n);
                      initGame(n, 1000, selectedVariation);
                    }}
                    className="bg-slate-800 text-white rounded px-2 py-1 text-xs"
                    data-testid="players-select"
                  >
                    <option value={4}>4P</option>
                    <option value={5}>5P</option>
                    <option value={6}>6P</option>
                  </select>
                  <select
                    value={potLimit || 'none'}
                    onChange={(e) => {
                      const val = e.target.value === 'none' ? null : parseInt(e.target.value);
                      setPotLimit(val);
                      setStorePotLimit(val);
                    }}
                    className="bg-slate-800 text-white rounded px-2 py-1 text-xs"
                    data-testid="pot-limit-select"
                  >
                    <option value="none">No Limit</option>
                    <option value="500">₹500</option>
                    <option value="1000">₹1k</option>
                    <option value="2000">₹2k</option>
                  </select>
                </div>
                
                <Button
                  onClick={startHand}
                  className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 font-bold py-3"
                  data-testid="deal-btn"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Deal Cards
                </Button>
              </div>
            ) : gamePhase === GAME_PHASE.DEALING ? (
              <div className="bg-slate-800/80 rounded-xl p-3 text-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="inline-block"
                >
                  <Sparkles className="w-6 h-6 text-amber-400" />
                </motion.div>
                <div className="text-amber-400 mt-1 text-sm">Dealing...</div>
              </div>
            ) : gamePhase === GAME_PHASE.BETTING && humanPlayer ? (
              <ActionPanel
                player={humanPlayer}
                currentStake={currentStake}
                config={config}
                onSeeCards={seeCards}
                onBet={placeBet}
                onPack={pack}
                onSideShow={requestSideShow}
                onShow={requestShow}
                canSideShow={canSideShow}
                canShow={canShow}
                disabled={!isHumanTurn}
              />
            ) : gamePhase === GAME_PHASE.SETTLING ? (
              <Button
                onClick={nextHand}
                className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 font-bold"
              >
                Next Hand
              </Button>
            ) : (
              <div className="bg-slate-800/80 rounded-xl p-4 text-center text-slate-400">
                {message || 'Please wait...'}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Side Show Modal */}
      <AnimatePresence>
        {sideShowRequest && gamePhase === GAME_PHASE.SIDE_SHOW && (
          <SideShowModal
            request={sideShowRequest}
            players={players}
            onAccept={() => respondToSideShow(true)}
            onDecline={() => respondToSideShow(false)}
            isResponder={sideShowRequest.toIndex === 0}
          />
        )}
      </AnimatePresence>
      
      {/* Winner Overlay */}
      <AnimatePresence>
        {winner && gamePhase === GAME_PHASE.SETTLING && (
          <WinnerOverlay
            winner={winner}
            pot={winner.chips - (players.find(p => p.id === winner.id)?.chips || 0) + pot}
            showdownHands={showdownHands}
            players={players}
            onNextHand={nextHand}
          />
        )}
      </AnimatePresence>
      
      {/* Help Modal */}
      <AnimatePresence>
        {showHelp && <HelpModal onClose={() => setShowHelp(false)} />}
      </AnimatePresence>
      
      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <SettingsModal 
            onClose={() => setShowSettings(false)} 
            variation={selectedVariation}
            onVariationChange={(v) => {
              setSelectedVariation(v);
              initGame(numPlayers, 1000, v);
              setShowSettings(false);
            }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeenPatti;
